/**
 * /settings/guardrails/review — the formatted draft awaiting approval. The
 * draft exists only in memory (nothing is stored until saved), so it arrives
 * via navigation state; landing here without one — a reload, a pasted link —
 * redirects to the list.
 */
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import { ReviewUploadView } from './ReviewUploadView.js';
import { baseNameOf } from './fileExtract.js';
import { GUARDRAILS_PATH, type ReviewLocationState } from './shared.js';

export const ReviewUploadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;

  const state = (location.state ?? null) as ReviewLocationState | null;
  if (!state?.draft) return <Navigate to={GUARDRAILS_PATH} replace />;

  if (meQuery.isLoading || !tenantId) {
    return (
      <AppShell>
        <Skeleton className="mb-6 h-5 w-64" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <ReviewUploadView
        tenantId={tenantId}
        draft={state.draft}
        initialName={baseNameOf(state.draft.filename)}
        onSaved={() => navigate(GUARDRAILS_PATH)}
        onSavedAddRules={(policy) =>
          navigate(`${GUARDRAILS_PATH}/add?to=${encodeURIComponent(policy.id)}`)
        }
        onDiscard={() => navigate(GUARDRAILS_PATH)}
      />
    </AppShell>
  );
};
